import type {AnyPcbBakedNode, PcbRuleSetsWithSpecificityMap} from "@buildwithflux/core";
import type {IDocumentData, PcbRulesMap, PcbRuleSet} from "@buildwithflux/models";
import {isEqual} from "lodash";
import {useCallback} from "react";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "../../../../injection/hooks";
import {selectRuleSetsForNode} from "../../../reactivity/selectors/pcb";
import {select} from "../selectors";

/**
 * Hook to get all PCB layout rule sets from the document
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const usePcbLayoutRuleSets = (): IDocumentData["pcbLayoutRuleSets"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.document.pcbLayoutRuleSets));
};

/**
 * Hook to get only the global PCB layout rules from the document
 * Global rules are those that have a referenceOfDocumentUid property
 * Delegates to the memoized globalPcbLayoutRules selector.
 */
export const useGlobalPcbLayoutRules = (): IDocumentData["pcbLayoutRuleSets"] | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.pcbLayoutRules.globalRules));
};

/**
 * Hook to check if a PCB layout rule set is disabled
 * Delegates to the pcbLayoutRuleSetDisabled selector.
 * @param ruleUid The UID of the rule set to check
 * @returns True if the rule set is disabled, false otherwise
 */
export const usePcbLayoutRuleSetDisabled = (ruleUid: string): boolean => {
    return useFluxServices().useDocumentStore(select.pcbLayoutRules.disabledByUid(ruleUid));
};

/**
 * Hook to get a global PCB layout rule set by UID
 * @param ruleUid The UID of the rule set to retrieve
 * @returns The rule set data or undefined if not found
 */
export const useGlobalPcbLayoutRuleSet = (ruleUid: string): PcbRuleSet | undefined => {
    return useFluxServices().useDocumentStore(select.pcbLayoutRules.byUid(ruleUid));
};

/**
 * Hook to get the object-specific rules for a PCB layout node
 * Uses shallow comparison to prevent unnecessary re-renders
 * @param nodeUid The UID of the node to get rules for
 * @returns The node's rule set or an empty object if not found
 */
export const useNodeObjectSpecificRules = (nodeUid: string): PcbRulesMap => {
    return useFluxServices().useDocumentStore(useShallow(select.pcbLayoutRules.nodeSpecificRules(nodeUid)));
};

/**
 * Hook to create a memoized selector for getting rule sets that apply to a PCB node.
 * This wraps the selectRuleSetsForNode selector with proper memoization to avoid
 * creating new selector functions on every render.
 *
 * @param globalRules The global PCB layout rule sets from the document
 * @returns A memoized selector function that can be used with usePcbNode
 */
export const useRuleSetsForNodeSelector = (
    globalRules: IDocumentData["pcbLayoutRuleSets"] | undefined,
): ((node: AnyPcbBakedNode | undefined) => PcbRuleSetsWithSpecificityMap) => {
    const {documentService, useDocumentStore} = useFluxServices();

    return useCallback(
        (node: AnyPcbBakedNode | undefined) =>
            selectRuleSetsForNode(node, globalRules, documentService, useDocumentStore),
        [globalRules, documentService, useDocumentStore],
    );
};

/**
 * Hook to get rule sets for a PCB node using usePcbNode with proper memoization.
 * This is a convenience hook that combines useRuleSetsForNodeSelector with usePcbNode.
 *
 * @param nodeUid The UID of the node to get rule sets for
 * @param globalRules The global PCB layout rule sets from the document
 * @returns The rule sets that apply to the node
 */
export const useRuleSetsForNode = (
    nodeUid: string,
    globalRules: IDocumentData["pcbLayoutRuleSets"] | undefined,
): PcbRuleSetsWithSpecificityMap => {
    const {usePcbNode} = useFluxServices();
    const selector = useRuleSetsForNodeSelector(globalRules);

    return usePcbNode(nodeUid, selector, isEqual);
};
